import { Alert, Box, Chip, Paper, Stack, Typography } from '@mui/material';
import { CampaignRounded, PaidRounded } from '@mui/icons-material';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { myAnnouncementsApi, type MyAnnouncement } from '../../../lib/api/announcement.api';
import { useTenantStore } from '../../../stores/tenant.store';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import { TuitionSummaryCard } from '../../sis/components/TuitionSummaryCard';
import { StudentScholarshipTab } from '../../portal/components/StudentScholarshipTab';
import { useEnrolledSections } from '../hooks/use-portal-data';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { formatDate } from '../../../lib/i18n-format';

// ─── Payments ─────────────────────────────────────────────────────────────────

export function PaymentsPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { personId } = useStudentIdentity();
  if (!personId) {
    return (
      <PortalEmpty
        icon={<PaidRounded fontSize="inherit" />}
        title={t('portal.placeholders.payments.noProfileTitle')}
        body={t('portal.placeholders.payments.noProfileBody')}
      />
    );
  }
  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h4" fontWeight={700} color="#e6edf3">
          {t('portal.placeholders.payments.title')}
        </Typography>
        <Typography variant="body2" color="#8b949e">
          {t('portal.placeholders.payments.description')}
        </Typography>
      </Box>
      <TuitionSummaryCard studentId={personId} />
    </Stack>
  );
}

// ─── Scholarships ─────────────────────────────────────────────────────────────

export function ScholarshipsPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { personId } = useStudentIdentity();
  const { data, isLoading } = useEnrolledSections();
  if (isLoading) return <PortalLoading />;
  const activeTermId = data?.activeTerm?.id ?? null;
  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography variant="h4" fontWeight={700} color="#e6edf3">
          {t('portal.placeholders.scholarships.title')}
        </Typography>
        <Typography variant="body2" color="#8b949e">
          {t('portal.placeholders.scholarships.description')}
        </Typography>
      </Box>
      <StudentScholarshipTab termId={activeTermId} studentId={personId ?? null} />
    </Stack>
  );
}

// ─── Announcements ────────────────────────────────────────────────────────────

// Los anuncios del alumno los elige el SERVIDOR (S0·SEC-1a): antes el portal
// traía todos los publicados del seminario —con sus listas de destinatarios— y
// filtraba aquí. La regla de Firestore ya no le deja leer la colección.
type AnnouncementItem = MyAnnouncement;

const READ_STORAGE_KEY = 'portal-v2-announcements-read';

function loadRead(): Set<string> {
  try {
    const raw = localStorage.getItem(READ_STORAGE_KEY);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function saveRead(set: Set<string>): void {
  try {
    localStorage.setItem(READ_STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    // ignore quota errors
  }
}

export function AnnouncementsPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { currentTenantId } = useTenantStore();
  const { personId } = useStudentIdentity();
  const announcementsQ = useQuery({
    queryKey: ['portal-v2-announcements', currentTenantId],
    enabled: !!currentTenantId && !!personId,
    queryFn: () => myAnnouncementsApi.listMine({ tenantId: currentTenantId! }),
  });

  const visible: AnnouncementItem[] = useMemo(
    () => (personId ? (announcementsQ.data ?? []) : []),
    [announcementsQ.data, personId],
  );

  const [readIds, setReadIds] = useState<Set<string>>(() => loadRead());
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    saveRead(readIds);
  }, [readIds]);

  const toggle = (id: string) => {
    setExpandedId((prev) => {
      const next = prev === id ? null : id;
      if (next) setReadIds((r) => new Set([...r, id]));
      return next;
    });
  };

  const markAllRead = () => setReadIds(new Set(visible.map((a) => a.id)));

  if (announcementsQ.isLoading) return <PortalLoading />;

  const unread = visible.filter((a) => !readIds.has(a.id)).length;

  return (
    <Stack spacing={2.5}>
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between">
        <Box>
          <Typography variant="h4" fontWeight={700} color="#e6edf3">
            {t('portal.placeholders.announcements.title')}
          </Typography>
          <Typography variant="body2" color="#8b949e">
            {t('portal.placeholders.announcements.description')}
          </Typography>
        </Box>
        {unread > 0 && (
          <Chip
            label={t('portal.placeholders.announcements.markAllRead', { count: unread })}
            size="small"
            onClick={markAllRead}
            sx={{
              bgcolor: 'rgba(99,102,241,0.12)',
              color: '#a5b4fc',
              border: '1px solid #4338ca',
              cursor: 'pointer',
              '&:hover': { bgcolor: 'rgba(99,102,241,0.2)' },
            }}
          />
        )}
      </Stack>

      {visible.length === 0 ? (
        <PortalEmpty
          icon={<CampaignRounded fontSize="inherit" />}
          title={t('portal.placeholders.announcements.emptyTitle')}
          body={t('portal.placeholders.announcements.emptyBody')}
        />
      ) : (
        <Paper
          variant="outlined"
          sx={{
            bgcolor: '#161b22',
            border: '1px solid #21262d',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          {visible.map((a) => {
            const isRead = readIds.has(a.id);
            const isExpanded = expandedId === a.id;
            return (
              <Box
                key={a.id}
                onClick={() => toggle(a.id)}
                sx={{
                  px: 2.5,
                  py: 1.75,
                  borderBottom: '1px solid #21262d',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  bgcolor: isExpanded ? 'rgba(99,102,241,0.06)' : 'transparent',
                  transition: 'background 0.15s',
                  '&:hover': {
                    bgcolor: isExpanded ? 'rgba(99,102,241,0.08)' : 'rgba(255,255,255,0.03)',
                  },
                  '&:last-child': { borderBottom: 'none' },
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    mt: 1,
                    borderRadius: '50%',
                    bgcolor: isRead ? 'transparent' : '#6366f1',
                    flexShrink: 0,
                    boxShadow: isRead ? 'none' : '0 0 6px rgba(99,102,241,0.6)',
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    fontWeight={isRead ? 500 : 700}
                    color={isRead ? '#8b949e' : '#e6edf3'}
                  >
                    {a.title}
                  </Typography>
                  <Typography variant="caption" color="#555e6d">
                    {a.publishedAt
                      ? formatDate(a.publishedAt, {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })
                      : ''}
                  </Typography>
                  {isExpanded && (
                    <Box
                      sx={{
                        mt: 1.5,
                        color: '#c9d1d9',
                        fontSize: 14,
                        lineHeight: 1.6,
                        '& a': { color: '#79c0ff' },
                      }}
                      dangerouslySetInnerHTML={{ __html: a.content }}
                    />
                  )}
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}

      {announcementsQ.isError && (
        <Alert severity="error">{t('portal.placeholders.announcements.loadError')}</Alert>
      )}
    </Stack>
  );
}
